'use client';

import { Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import {
  SETUP_PATH_MOTION,
  SETUP_PATH_OPTIONS,
  type SetupSourceOptionDefinition,
} from '@/components/setup/experience/constants';
import { SetupStepSection } from '@/components/setup/experience/SetupStepSection';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import type { SetupSourcePath } from '@/components/setup/experience/types';
import { brandingConfig } from '@/config/branding';
import { cn } from '@/lib/utils';

interface SetupPathStepProps {
  selectedPath: SetupSourcePath | null;
  onSelectPath: (path: SetupSourcePath) => void;
  onContinue: () => void;
  onBack: () => void;
  canContinue: boolean;
}

function SourceLogo({ path }: { path: SetupSourcePath }) {
  if (path === 'manual') {
    const { logo, logoDark, defaultIntegrationLogo } = brandingConfig;
    const lightSrc = defaultIntegrationLogo ?? logo;
    const darkSrc = logoDark ?? lightSrc;

    return (
      <span
        className="bg-muted flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.625rem]"
        aria-hidden
      >
        <img src={lightSrc} alt="" className="h-6 w-6 object-contain dark:hidden" draggable={false} />
        <img src={darkSrc} alt="" className="hidden h-6 w-6 object-contain dark:block" draggable={false} />
      </span>
    );
  }

  const src = path === 'shopify' ? '/integrations/shopify-icon.svg' : '/integrations/woo-icon.svg';

  return (
    <span
      className="bg-muted flex h-11 w-11 shrink-0 items-center justify-center rounded-[0.625rem]"
      aria-hidden
    >
      <img src={src} alt="" className="h-6 w-6 object-contain" draggable={false} />
    </span>
  );
}

function PathOptionRow({
  option,
  isSelected,
  onSelect,
  t,
}: {
  option: SetupSourceOptionDefinition;
  isSelected: boolean;
  onSelect: () => void;
  t: (key: string) => string;
}) {
  return (
    <motion.li variants={SETUP_PATH_MOTION.option}>
      <button
        type="button"
        role="option"
        aria-selected={isSelected}
        className={cn(
          'border-border bg-card flex min-h-[4.75rem] w-full cursor-pointer items-center gap-3.5 rounded-[0.625rem] border px-[1.125rem] py-4 text-left transition-[border-color,background,box-shadow] duration-150',
          !isSelected &&
            'hover:border-[color-mix(in_srgb,var(--primary)_25%,var(--border))] hover:bg-[color-mix(in_srgb,var(--muted)_35%,var(--card))]',
          isSelected &&
            'border-[var(--crm-selection-border-strong)] bg-[color-mix(in_srgb,var(--crm-primary-surface)_55%,var(--card))] shadow-[0_0_0_1px_var(--crm-selection-ring)]',
        )}
        onClick={onSelect}
      >
        <SourceLogo path={option.id} />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground text-sm font-semibold">{t(option.nameKey)}</span>
            {option.popular ? (
              <span className="text-primary rounded-full border border-[var(--crm-primary-surface-border)] bg-[var(--crm-primary-surface)] px-[0.4375rem] py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.06em]">
                {t('experience.path.popularBadge')}
              </span>
            ) : null}
          </span>
          <span className="text-[0.8125rem] leading-[1.45] text-[color-mix(in_oklch,var(--foreground)_72%,var(--muted-foreground))]">
            {t(option.hintKey)}
          </span>
        </span>
        <span className="flex shrink-0 items-center justify-center" aria-hidden>
          {isSelected ? (
            <span className="text-primary-foreground bg-primary flex h-5 w-5 shrink-0 items-center justify-center rounded-full">
              <Check className="size-3" strokeWidth={3} />
            </span>
          ) : (
            <span className="h-5 w-5 shrink-0 rounded-full border-[1.5px] border-[color-mix(in_srgb,var(--muted-foreground)_45%,transparent)]" />
          )}
        </span>
      </button>
    </motion.li>
  );
}

export function SetupPathStep({
  selectedPath,
  onSelectPath,
  onContinue,
  onBack,
  canContinue,
}: SetupPathStepProps) {
  const { t } = useTranslation('setup');

  return (
    <SetupStepSection
      title={t('experience.path.title')}
      description={t('experience.path.description')}
      footer={
        <SetupStepFooter
          backLabel={t('footer.back')}
          onBack={onBack}
          primaryLabel={t('footer.continue')}
          onPrimary={onContinue}
          primaryDisabled={!canContinue}
        />
      }
    >
      <motion.ul
        className="m-0 flex list-none flex-col gap-3 p-0"
        role="listbox"
        aria-label={t('experience.path.title')}
        variants={SETUP_PATH_MOTION.list}
        initial="hidden"
        animate="show"
      >
        {SETUP_PATH_OPTIONS.map((option) => (
          <PathOptionRow
            key={option.id}
            option={option}
            isSelected={selectedPath === option.id}
            onSelect={() => onSelectPath(option.id)}
            t={t}
          />
        ))}
      </motion.ul>
    </SetupStepSection>
  );
}
